import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * The second-level tab bar, shared by every Bruktbil page that has one.
 *
 * Four pages hand-rolled this, all four painted in the *same underline style
 * as the module's top-level tab bar*. Two identical rows of underline tabs
 * read as peers rather than as a hierarchy, and on Ståtid the labels collide
 * outright: the module has an "Oversikt" and a "Merke & modell", and so does
 * Ståtid. Nothing on screen said which level you were looking at.
 *
 * So this bar deliberately does not look like the one above it. A segmented
 * track with a raised active pill is visibly subordinate, and it stays clear
 * of both the cyan reserved for the Oppdater button and the tinted style that
 * means "selected filter" — a nav control should not read as either.
 *
 * The parent's name leads the row, which is what makes a second "Oversikt"
 * unambiguous: you are looking at `Ståtid: Oversikt`, not at the module's.
 */

interface SubTab<K extends string> {
  key: K
  label: string
  icon?: LucideIcon
}

interface Props<K extends string> {
  /** The top-level tab these belong to, e.g. "Ståtid". */
  parent: string
  tabs: readonly SubTab<K>[]
  active: K
  onChange: (key: K) => void
  /** Prefix for each tab's data-testid, e.g. "staatid-tab-". */
  testIdPrefix: string
}

export default function SubTabBar<K extends string>({
  parent, tabs, active, onChange, testIdPrefix,
}: Props<K>) {
  const { t: tr } = useTranslation('bruktbil')
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="bruktbil-sub-tabs">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {parent}
      </span>
      <div
        role="tablist"
        aria-label={tr('subTabBar.ariaLabel', { parent })}
        className="inline-flex gap-1 p-0.5 rounded-lg bg-gray-100 dark:bg-dark-card overflow-x-auto"
      >
        {tabs.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={active === t.key}
            data-testid={`${testIdPrefix}${t.key}`}
            onClick={() => onChange(t.key)}
            className={`px-3 py-1 rounded-md text-sm transition-colors whitespace-nowrap ${
              active === t.key
                ? 'bg-white dark:bg-dark-bg text-gray-900 dark:text-gray-100 font-medium shadow-xs'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <span className="flex items-center gap-1.5">
              {t.icon && <t.icon className="w-3.5 h-3.5" />}
              {t.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
